import { useEffect, useState, type JSX } from 'react';
import { PRESETS, type PresetKey } from '@nightshift/engine/client';
import { linkProps } from '../app/router.ts';
import { api } from '../net/api.ts';
import { AXES, Calibration } from './Debrief.tsx';
import { groupResults, type Cohort, type CohortGroup, type CohortResult } from './cohort-summary.ts';

const metrics = AXES.filter((axis) => ['saved', 'harmed', 'died', 'handedOver', 'early', 'bounceBacks', 'overflowDelays',
  'plannedEscalations', 'crisisEscalations', 'lateEscalations', 'idleCapacity', 'resources'].includes(axis.k));

/** Show saved table results without ranking individual participants. */
export function CohortComparison({ id, currentSession }: { id: string; currentSession: string | null }): JSX.Element {
  const [data, setData] = useState<{ cohort: Cohort; rows: CohortResult[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState('');
  const [seats, setSeats] = useState('');
  useEffect(() => {
    let active = true;
    setData(null);
    setError(null);
    Promise.all([api<Cohort[]>('/api/cohorts'), api<CohortResult[]>(`/api/cohorts/${encodeURIComponent(id)}/results`)])
      .then(([cohorts, rows]) => {
        const cohort = cohorts.find((item) => item.id === id);
        if (!cohort) throw new Error('No such cohort.');
        if (active) setData({ cohort, rows });
      })
      .catch((cause: unknown) => {
        console.warn(`[nightshift] cohort ${id} could not load`, cause);
        if (active) setError(cause instanceof Error ? cause.message : 'Could not load this cohort.');
      });
    return () => { active = false; };
  }, [id]);
  const groups = groupResults((data?.rows ?? []).filter((row) => (!preset || row.preset === preset) && (!seats || row.seats === Number(seats))));
  return <main className="fac-page">
    <header className="fac-top"><a {...linkProps('/control')}>← Control</a><a className="btn btn-small" href={`/api/cohorts/${encodeURIComponent(id)}/results.csv`} download>Download cohort CSV</a></header>
    <div className="debrief" style={{ padding: 20 }}>
      <h1 className="display">{data?.cohort.name ?? 'Cohort comparison'}</h1>
      <p className="muted">Saved results by table. Different presets, seat counts, engine versions, pacing or optional layers are compared separately. Early and unknown endings stay within their session.</p>
      {error && <p role="alert" className="form-error">{error}</p>}
      {!data && !error && <p role="status">Loading saved tables…</p>}
      {data && <>
        <div className="field-row">
          <div className="field"><label htmlFor="cohort-preset">Preset filter</label><select id="cohort-preset" className="input" value={preset} onChange={(event) => setPreset(event.target.value)}>
            <option value="">All presets</option>{Object.entries(PRESETS).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}
          </select></div>
          <div className="field"><label htmlFor="cohort-seats">Seats filter</label><select id="cohort-seats" className="input" value={seats} onChange={(event) => setSeats(event.target.value)}>
            <option value="">All seat counts</option><option value="4">4 seats</option><option value="5">5 seats</option>
          </select></div>
        </div>
        {groups.length === 0 && <p className="empty">No saved tables match these filters.</p>}
        {groups.map((group) => <ResultGroup key={group.key} group={group} currentSession={currentSession} />)}
      </>}
    </div>
  </main>;
}

/** Display one comparable set of tables, its mean and pooled calibration. */
export function ResultGroup({ group, currentSession }: { group: CohortGroup; currentSession: string | null }): JSX.Element {
  const first = group.rows[0];
  const p = first.pacingConfig;
  const draftTurns = first.engineVersion.split('.')[0] === '3';
  const layers = Object.entries(first.layers).filter(([, on]) => on).map(([name]) => name).join(', ') || 'none';
  return <section className="fac-panel" data-testid="cohort-group">
    <h2 className="display">{PRESETS[first.preset as PresetKey]?.label ?? first.preset} · {first.seats} seats · engine {first.engineVersion} · {first.completion === 'full' ? 'Full shift' : first.completion === 'early' ? 'Ended early' : 'Completion unknown'}</h2>
    <p className="muted small">Pacing {first.pacing} ({p.key}); script turns {p.scriptTurns.join(', ')}. {draftTurns
      ? <>Action: {p.actMs / 1000} seconds. Review: untimed; Control starts the next turn. Handover: {p.handoverMs / 1000} seconds.</>
      : <>Phase durations in seconds: arrivals {p.arrivalsMs / 1000}, huddle {p.huddleMs / 1000}, act {p.actMs / 1000}, tick {p.tickMs / 1000}, log {p.logMs / 1000}, handover {p.handoverMs / 1000}.</>} Layers: {layers}.</p>
    <table className="fac-table">
      <caption>Tables with identical settings and completion. Means weight each table equally. A dash means the measure was not recorded for every table.</caption>
      <thead><tr><th scope="col">Ended / table</th>{metrics.map((axis) => <th scope="col" key={axis.k}>{axis.label}</th>)}<th scope="col">Objectives met / dealt</th></tr></thead>
      <tbody>{group.rows.map((row) => <tr key={`${row.sessionId}-${row.table}`} data-testid={`cohort-row-${row.sessionId}-${row.table}`}
        aria-current={row.sessionId === currentSession ? 'true' : undefined} style={row.sessionId === currentSession ? { background: 'var(--night-3)' } : undefined}>
        <th scope="row"><a {...linkProps(`/control/s/${row.sessionId}/replay`)}>{new Date(row.endedAt).toLocaleString('en-AU')} · Table {row.table}</a>{row.sessionId === currentSession && ' (current)'}</th>
        {metrics.map((axis) => <td key={axis.k} data-metric={axis.k}>{row.dashboard[axis.k] ?? '—'}</td>)}
        <td>{row.dashboard.objectives} / {row.dashboard.objectivesDealt}</td>
      </tr>)}</tbody>
      <tfoot><tr data-testid="cohort-mean"><th scope="row">Mean ({group.rows.length} tables)</th>
        {metrics.map((axis) => <td key={axis.k} data-metric={axis.k}>{group.mean[axis.k]?.toLocaleString('en-AU', { maximumFractionDigits: 2 }) ?? '—'}</td>)}
        <td>{group.mean.objectives.toLocaleString('en-AU', { maximumFractionDigits: 2 })} / {group.mean.objectivesDealt.toLocaleString('en-AU', { maximumFractionDigits: 2 })}</td>
      </tr></tfoot>
    </table>
    <div className="db-split">
      <section><h3 className="sec-title">Harmed + Died over time</h3><OutcomeTrend group={group} /></section>
      <section><h3 className="sec-title">Pooled calibration</h3><p className="small muted">Correct cards divided by all cards drawn in this group, grouped by how sure each card said it was on arrival.</p><Calibration rows={group.calibration} /></section>
    </div>
  </section>;
}

/** Plot combined harm and deaths against completion date with accessible totals. */
function OutcomeTrend({ group }: { group: CohortGroup }): JSX.Element {
  const first = group.rows[0].endedAt;
  const last = group.rows[group.rows.length - 1].endedAt;
  const max = group.rows.reduce((high, row) => Math.max(high, row.dashboard.harmed + row.dashboard.died), 1);
  const x = (at: number): number => first === last ? 185 : 35 + (at - first) * 300 / (last - first);
  const y = (value: number): number => 150 - value * 120 / max;
  return <figure className="cal">
    <svg viewBox="0 0 370 195" role="img" aria-label="Harm and death by completion date: combined total">
      <line x1="35" x2="335" y1="150" y2="150" className="cal-grid" />
      <text x="5" y="154" className="cal-ax">0</text><text x="5" y="34" className="cal-ax">{max}</text>
      <g stroke="var(--lamp)">
        <polyline points={group.rows.map((row) => `${x(row.endedAt)},${y(row.dashboard.harmed + row.dashboard.died)}`).join(' ')} fill="none" strokeWidth="2" />
        {group.rows.map((row) => <circle key={`${row.sessionId}-${row.table}`} cx={x(row.endedAt)} cy={y(row.dashboard.harmed + row.dashboard.died)} r="3" fill="currentColor">
          <title>{new Date(row.endedAt).toLocaleString('en-AU')}, table {row.table}: {row.dashboard.harmed + row.dashboard.died} harmed + died</title>
        </circle>)}
      </g>
      <text x="35" y="178" className="cal-ax">{new Date(first).toLocaleDateString('en-AU')}</text>
      <text x="335" y="192" textAnchor="end" className="cal-ax">{new Date(last).toLocaleDateString('en-AU')}</text>
    </svg>
    <figcaption className="small muted">Harmed + Died total, one point per table. Individual measures are in the results table.</figcaption>
  </figure>;
}
